import type { ThemeDoc } from '@event67/shared';
import { useEffect } from 'react';
import { fontHref, installFontLink } from '../lib/fonts.js';
import { themeCss } from '../lib/theme.js';

/**
 * Wears the event's theme.
 *
 * The variables go in as a real `<style>` element rather than an inline `style`
 * attribute on a wrapper, for two reasons: `@media (prefers-color-scheme: dark)`
 * cannot live in an inline style, and the page background is painted by `body`,
 * which no wrapper can reach.
 *
 * `dangerouslySetInnerHTML` is the only way to put CSS text in an element, and
 * is safe here because `themeCss` builds every declaration from a validated
 * `#RRGGBB`, a clamped integer, or a name from the font allowlist — see the
 * checks in `src/lib/theme.ts` and the hostile-theme test beside them.
 */
export function ThemeStyle({ theme }: { theme: ThemeDoc }) {
  const href = fontHref(theme);

  useEffect(() => {
    installFontLink(document, href);
  }, [href]);

  // The suppression is one line because Biome only reads the line directly
  // above; the reasoning is in the doc comment on this component.
  return (
    // biome-ignore lint/security/noDangerouslySetInnerHtml: CSS text has no other API, and every value themeCss() interpolates is an allowlisted hex, a clamped integer, or a font from the theme enum — see theme.test.ts, "cannot be made to emit anything but hex, px, numbers and a quoted font".
    <style data-e67-theme={theme.name} dangerouslySetInnerHTML={{ __html: themeCss(theme) }} />
  );
}
